import { useEffect, useState } from "react";
import { Building2, Loader2, Users, AlertCircle, CheckCircle } from "lucide-react";

import {
  useGetMyFirmsQuery,
  useGetMyFirmQuery,
  usePayMyFirmDuesMutation,
} from "../api/firmApi";
import { submitOnePayForm } from "../../logbook/utils/onepay";
import { formatCurrency, formatDate } from "../../../utils/formatters";

/**
 * A firm as its OWNER sees it.
 *
 * Not the firm APPLICATION screen: that is paperwork moving through a review,
 * this is a firm that exists on the register. The owner is either the account
 * attached to the firm or its principal partner — the server decides which, and
 * a firm that is not theirs simply is not here.
 *
 * The firm's dues are the FIRM's, never the owner's own: they are shown here and
 * paid here, and they never appear in the owner's personal balance.
 */

function Row({ k, v }: { k: string; v: any }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-50 py-2.5 text-sm last:border-0">
      <div className="text-gray-400 font-medium uppercase text-[10px] tracking-wider shrink-0">{k}</div>
      <div className="text-gray-800 text-right break-words">{v || "—"}</div>
    </div>
  );
}

export default function MyFirmPage() {
  const { data: firms = [], isLoading, isError, error: listError } = useGetMyFirmsQuery();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // One firm is the common case — open it without making the owner choose.
  useEffect(() => {
    if (!selectedId && firms.length > 0) setSelectedId(firms[0].id);
  }, [firms, selectedId]);

  const { data: firm, isFetching } = useGetMyFirmQuery(selectedId as number, {
    skip: !selectedId,
  });

  const [pay, { isLoading: paying }] = usePayMyFirmDuesMutation();
  const [error, setError] = useState<string | null>(null);

  /**
   * Hand off to OnePay.
   *
   * Through the SHARED helper, not a form built here. OnePay expects its fields
   * in snake_case (`merchant_reg_id`, `amt_paid`, …); a hand-rolled form with
   * different names arrives carrying no merchant id at all, and the gateway
   * answers "INVALID Merchant/Caller" — which reads like a configuration problem
   * and is not one. Every other fee in the portal leaves through this function,
   * and firm dues now do too.
   */
  const payDues = async () => {
    if (!firm) return;
    setError(null);
    try {
      const res: any = await pay({ id: firm.id }).unwrap();

      if (!res?.payment_url) {
        throw new Error(res?.message || "Could not start the payment.");
      }

      submitOnePayForm(res);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Could not start the payment. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading your firm…</p>
      </div>
    );
  }

  // An error is NOT "you have no firm". Saying so would be a claim about their
  // data made on the strength of a request that never arrived — and it sends
  // somebody to the secretariat over what is actually a broken endpoint.
  if (isError) {
    const status = (listError as any)?.status;
    const detail = (listError as any)?.data?.message;

    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="bg-white border rounded-xl p-8 text-center space-y-3">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
          <h1 className="text-lg font-bold text-gray-800">Could not load your firm</h1>
          <p className="text-sm text-gray-500">
            {detail ||
              "The firm register did not answer. This is a problem with the request, not with your membership — please try again, and tell the secretariat if it persists."}
          </p>
          {status && (
            <p className="text-[11px] font-mono text-gray-400">
              {String(status)} · GET /firm/my-firms
            </p>
          )}
        </div>
      </div>
    );
  }

  if (firms.length === 0) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="bg-white border rounded-xl p-8 text-center space-y-3">
          <Building2 className="w-8 h-8 text-gray-300 mx-auto" />
          <h1 className="text-lg font-bold text-gray-800">No firm is registered to you</h1>
          <p className="text-sm text-gray-500">
            This page shows a practice firm you own — one where you are the principal partner, or
            whose account is yours. If you believe a firm should be here, ask the secretariat to
            attach you to it.
          </p>
        </div>
      </div>
    );
  }

  const owed = Number(firm?.outstanding_kobo ?? 0);

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      {/* More than one firm is rare; a picker only appears when it is real. */}
      {firms.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {firms.map((f: any) => (
            <button
              key={f.id}
              onClick={() => setSelectedId(f.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                selectedId === f.id
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f.firm_no || f.name}
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-emerald-100 text-xs font-semibold mb-1.5 bg-white/10 px-2.5 py-1 rounded-full">
              <Building2 className="w-3.5 h-3.5" /> Registered Firm
            </div>
            <h1 className="text-2xl font-bold">{firm?.name ?? "—"}</h1>
            <p className="text-sm text-emerald-100 mt-1">
              {firm?.firm_no ?? "No firm number"}
              {firm?.approved_at ? ` · registered ${formatDate(firm.approved_at)}` : ""}
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${
              firm?.status === "active" ? "bg-white/20" : "bg-black/20"
            }`}
          >
            {firm?.status ?? "—"}
          </span>
        </div>
      </div>

      {/* What the firm owes — the firm's money, not the owner's. */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Firm dues outstanding
            </p>
            <p className={`text-3xl font-bold mt-1 ${owed > 0 ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(owed)}
            </p>
            <p className="text-[11px] text-gray-400 mt-1">
              Billed to the firm. This does not form part of your own membership dues.
            </p>
            <p className="text-[11px] text-gray-400">
              Paying at the bank instead? Send the teller to the secretariat and they will record
              it against the firm.
            </p>
          </div>

          {owed > 0 ? (
            <button
              onClick={payDues}
              disabled={paying || isFetching}
              className="px-6 py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 transition-colors"
            >
              {paying ? "Starting payment…" : `Pay ${formatCurrency(owed)}`}
            </button>
          ) : (
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-700">
              <CheckCircle className="w-4 h-4" /> Nothing outstanding
            </span>
          )}
        </div>

        {error && (
          <div className="mt-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
          </div>
        )}

        {/* What the balance is made of — a firm paying a bill is owed the sight
            of what it is paying for. */}
        {Array.isArray(firm?.invoices) && firm.invoices.length > 0 && (
          <div className="mt-5 border rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="px-4 py-2 font-medium">Invoice</th>
                  <th className="px-4 py-2 font-medium">For</th>
                  <th className="px-4 py-2 font-medium text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {firm.invoices.map((inv: any) => (
                  <tr key={inv.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{inv.invoice_number}</div>
                      <div className="text-[11px] text-gray-400">
                        {String(inv.source ?? "").replace(/_/g, " ")}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {(inv.items ?? [])
                        .map((i: any) => (i.year ? `${i.description} ${i.year}` : i.description))
                        .join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      {formatCurrency(inv.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Particulars */}
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-sm font-bold text-gray-800 mb-3">Firm details</h2>
          <Row k="Firm number" v={firm?.firm_no} />
          <Row k="Nature of practice" v={firm?.nature_of_practice} />
          <Row k="CAC / Business no" v={firm?.cama_reg_number} />
          <Row k="Email" v={firm?.email} />
          <Row k="Phone" v={firm?.phone} />
          <Row k="Registered address" v={firm?.registered_address} />
          <Row k="State" v={firm?.state} />
          <Row k="Chapter" v={firm?.chapter} />
          <Row k="Principal" v={firm?.principal?.name} />
        </div>

        {/* Roster */}
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> Partners
          </h2>
          {Array.isArray(firm?.partners) && firm.partners.length > 0 ? (
            <ul className="space-y-2">
              {firm.partners.map((p: any, i: number) => (
                <li key={i} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-800">{p.name ?? "—"}</span>
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {p.role}
                    {p.membership_no ? ` · ${p.membership_no}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">
              Nobody is on this firm's roster yet. The secretariat maintains it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
